import type { ModeBarButton, Shape } from 'plotly.js';
import React from 'react';
import { Suspense, lazy, useState, useEffect, useMemo } from 'react';
import { useTheme } from 'styled-components';

const PlotPlaceholder: React.FC<any> = ({ layout }) => {
  // Extrai o título de forma segura
  const getTitle = () => {
    if (!layout?.title) return 'Gráfico';
    if (typeof layout.title === 'string') return layout.title;
    if (typeof layout.title === 'object' && layout.title.text) return layout.title.text;
    return 'Gráfico';
  };

  return (
    <div
      style={{
        width: '100%',
        height: '400px',
        border: '2px dashed #ccc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: '#f9f9f9',
      }}>
      <h3>{getTitle()}</h3>
      <p>Plotly.js não disponível no momento</p>
    </div>
  );
};

const PlotlyWrapper: React.FC<any> = (props) => {
  const plotRef = React.useRef<HTMLDivElement>(null);
  const isInitializedRef = React.useRef(false);
  const layoutRef = React.useRef(props.layout);

  React.useEffect(() => {
    if (plotRef.current && typeof window !== 'undefined' && (window as any).Plotly) {
      if (!isInitializedRef.current) {
        console.log('Inicializando gráfico com Plotly CDN...');
        (window as any).Plotly.newPlot(plotRef.current, props.data, layoutRef.current, props.config);
        isInitializedRef.current = true;

        if (props.onButtonClicked) {
          (plotRef.current as any).on('plotly_buttonclicked', props.onButtonClicked);
        }
      }
    }
  }, []);

  React.useEffect(() => {
    if (plotRef.current && isInitializedRef.current && typeof window !== 'undefined' && (window as any).Plotly) {
      console.log('Atualizando dados do gráfico...');
      (window as any).Plotly.restyle(plotRef.current, {
        x: props.data.map((d: any) => d.x),
        y: props.data.map((d: any) => d.y),
      });
    }
  }, [props.data]);

  return <div ref={plotRef} style={props.style} />;
};

const LazyPlot = lazy(async () => {
  try {
    console.log('Tentando carregar react-plotly.js...');
    const module = await import('react-plotly.js');
    console.log('react-plotly.js carregado com sucesso!');
    return { default: module.default };
  } catch (error) {
    console.error('Erro ao carregar react-plotly.js:', error);
    console.log('Usando wrapper customizado do Plotly...');
    return { default: PlotlyWrapper as any };
  }
});

import { GeneratedChart } from '../../scripts/api/types';
import { TabItem, MenuItem, menuIcons, assertHasActive, parseX, colorOfCurve } from '../../scripts/common/chart';
import { useMedia } from '../../scripts/hooks/mediaQueryHook';

export type ChartProps = {
  /**
   * Chart options and data to be displayed to the user
   */
  layout: GeneratedChart;
  /**
   * Menu items are normally icons at the top right side of the chart
   */
  menu?: (MenuItem | undefined)[];
  /**
   * Tabs are button-like elements within the chart
   */
  tabs?: TabItem[];
  /**
   * Which tab is active, if there tabs isn't undefined
   */
  activeTab?: number;
  /**
   * Change the revision to force the update and reset of the ui (layout
   * buttons, zoom, etc)
   */
  uiRevision?: string;
  /**
   * If true, the plot is completely static, you can't move or zoom on it.
   */
  shoudPlotBeStatic?: boolean;
  /**
   * Shapes to be drawn inside the chart, as any shape allowed by plotly
   */
  shapes?: Partial<Shape>[];
};

const SChartComponent: React.FC<ChartProps> = ({
  layout: { title, axis, type, review = false },
  shoudPlotBeStatic = false,
  uiRevision = 'keep',
  activeTab = 0,
  tabs = [],
  menu = [],
  shapes,
}: ChartProps) => {
  const theme = useTheme();
  const isMobile = useMedia('(max-width: 600px)');
  const [revision] = useState(uiRevision);

  useEffect(() => {});

  const dataSet = useMemo(
    () =>
      axis?.map(({ X, Y = [], label }, i) => ({
        name: label,
        x: X ? parseX(X) : undefined,
        y: Y,
        mode: type === 'line' ? ('lines' as const) : undefined,
        type: type === 'bar' ? ('bar' as const) : undefined,
        line: { color: colorOfCurve(type, i, theme), width: 3 },
        marker: { color: colorOfCurve(type, i, theme) },
      })),
    [axis, type, theme]
  );

  return (
    <Suspense fallback={<div>Carregando gráfico...</div>}>
      <LazyPlot
        useResizeHandler={true}
        style={{ width: '100%' }}
        data={dataSet}
        config={useMemo(
          () => ({
            responsive: false,
            displaylogo: false,
            staticPlot: shoudPlotBeStatic,
            displayModeBar: menu !== undefined && menu.length > 0,
            locale: 'pt-BR',
            modeBarButtons: menu
              .filter<MenuItem>((item): item is MenuItem => item !== undefined)
              .filter((item) => typeof item === 'string' || item.visible)
              .map((item) => {
                if (typeof item === 'string') return [item];
                return [
                  {
                    name: item.icon,
                    icon: menuIcons[item.icon],
                    title: item.hint,
                    click: () => item.click(),
                  } as ModeBarButton,
                ];
              }),
          }),
          [menu, shoudPlotBeStatic]
        )}
        layout={useMemo(
          () => ({
            ...{
              uirevision: revision,
              modebar: {
                color: theme.pallete.primary.main,
                activecolor: theme.pallete.primary.light,
              },
            },
            updatemenus: [
              {
                buttons: tabs.map((tab) => {
                  if (tab.formatting)
                    return {
                      args: [
                        { visible: tab.formatting.toggleAxis.map((e) => e === 1) },
                        { title: tab.formatting.title },
                      ],
                      label: tab.label,
                      method: 'update',
                    };
                  return {
                    label: tab.label,
                    method: 'skip',
                  };
                }),
                bgcolor: theme.pallete.neutral.light,
                font: {
                  color: theme.pallete.border.main,
                  size: 14,
                },
                direction: 'right',
                pad: { r: -60, t: isMobile ? -40 : -60 },
                showactive: true,
                active: activeTab,
                type: 'buttons',
                x: 1,
                xanchor: 'right',
                y: 1,
                yanchor: 'top',
              },
            ],
            shapes,
            autosize: true,
            title: {
              x: 0.07,
              font: {
                size: 16,
                color: theme.pallete.neutral.main,
              },
              text: title,
            },
            legend: {
              xanchor: 'auto',
              yanchor: 'top',
              y: -0.2,
              orientation: 'h',
            },
            plot_bgcolor: review ? theme.pallete.neutral.lighter : '#f3f4f8',
            paper_bgcolor: review ? theme.pallete.neutral.lighter : '#f3f4f8',
          }),
          [revision, theme, tabs, activeTab, isMobile, shapes, title, review]
        )}
        onButtonClicked={(item: any) => {
          assertHasActive(item);
          if (tabs && typeof item.active === 'number') tabs[item.active].click();
        }}
      />
    </Suspense>
  );
};

export const SChart = React.memo(SChartComponent, (prevProps, nextProps) => {
  // Retorna true se as props são iguais (não deve re-renderizar)
  return (
    prevProps.layout.title === nextProps.layout.title &&
    prevProps.activeTab === nextProps.activeTab &&
    prevProps.shoudPlotBeStatic === nextProps.shoudPlotBeStatic &&
    prevProps.uiRevision === nextProps.uiRevision &&
    JSON.stringify(prevProps.layout.axis) === JSON.stringify(nextProps.layout.axis) &&
    JSON.stringify(prevProps.shapes) === JSON.stringify(nextProps.shapes)
  );
});

export default SChart;
